import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter,
    Input, OnDestroy, OnInit, Output, ViewRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
// import type { Locale } from '@uppy/locales';

// Import Uppy locale packs
import Arabic from '@uppy/locales/lib/ar_SA';
import Chinese from '@uppy/locales/lib/zh_CN';
import German from '@uppy/locales/lib/de_DE';
import Greek from '@uppy/locales/lib/el_GR';
import English from '@uppy/locales/lib/en_US';
import Spanish from '@uppy/locales/lib/es_ES';
import French from '@uppy/locales/lib/fr_FR';
import Hebrew from '@uppy/locales/lib/he_IL';
import Indonesian from '@uppy/locales/lib/id_ID';
import Italian from '@uppy/locales/lib/it_IT';
import Japanese from '@uppy/locales/lib/ja_JP';
import Korean from '@uppy/locales/lib/ko_KR';
import Malay from '@uppy/locales/lib/ms_MY';
import Dutch from '@uppy/locales/lib/nl_NL';
import Polish from '@uppy/locales/lib/pl_PL';
import Portuguese from '@uppy/locales/lib/pt_PT';
import Russian from '@uppy/locales/lib/ru_RU';
import Turkish from '@uppy/locales/lib/tr_TR';
import Vietnamese from '@uppy/locales/lib/vi_VN';
import ChineseSimplified from '@uppy/locales/lib/zh_CN';
import Ukrainian from '@uppy/locales/lib/uk_UA';

const localeMap: { [key: string]: any } = {
    ar: Arabic,
    cj: Chinese,
    de: German,
    el: Greek,
    en: English,
    es: Spanish,
    fr: French,
    he: Hebrew,
    id: Indonesian,
    it: Italian,
    ja: Japanese,
    ko: Korean,
    ms: Malay,
    nl: Dutch,
    pl: Polish,
    pt: Portuguese,
    ru: Russian,
    tr: Turkish,
    vi: Vietnamese,
    zh: ChineseSimplified,
    uk: Ukrainian,
  };

@Component({
    selector: 'uppy-file-importer',
    templateUrl: './uppy-file-importer.cmp.html',
    styleUrls: [ './uppy-file-importer.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UppyFileImporterComponent implements OnInit, AfterViewInit, OnDestroy {

    /**
     * Target element class name
     */
    @Input() target: string;

    /**
     * Uppy dashboard import options
     * Check: https://uppy.io/docs/dashboard/
     */
    @Input() uiOptions: any = {
        inline : true,
        replaceTargetContent : false,
    };

    /**
     * Uppy core import options
     * Check: https://uppy.io/docs/uppy/
     */
    @Input() importOptions: any = {
        allowMultipleUploads: true,
    };

    @Input() uploadEventTrigger: Subject<boolean> = new Subject();

    @Input() removeFileTrigger: Subject<string> = new Subject();

    @Output() onUploadComplete: EventEmitter<any> = new EventEmitter();

    // Uppy instance
    uppy: any;

    subs: Subscription[] = [];

    constructor(
        protected ref: ChangeDetectorRef,
        protected translate: TranslateService,
    ) {}

    @Input() afterViewInitCallback: Function = () => {};

    ngOnInit(): void {
        this.subs.push(
            this.listenUploadEventTrigger().subscribe(() => this.uploadEventHandler()),
            this.listenRemoveFileTrigger().subscribe(),
        );
    }

    ngAfterViewInit(): void {

        const currentLang = this.translate.currentLang || 'en';
        const selectedLocale = localeMap[currentLang];
        this.uppy = Uppy({
            ...this.importOptions,
            locale: selectedLocale,
        });
        this.uiOptions.target = '.' + this.target;
        this.uppy.use( Dashboard, this.uiOptions ).run();

        this.uppy.on( 'complete', (result: any) => {
            this.onUploadComplete.emit( result );
        });
        this.uppy.on( 'file-added', () => !( this.ref as ViewRef ).destroyed && this.ref.detectChanges());
        this.uppy.on( 'file-removed', () => !( this.ref as ViewRef ).destroyed && this.ref.detectChanges());

        this.afterViewInitCallback();
    }

    ngOnDestroy(): void {
        while ( this.subs.length > 0 ) {
            this.subs.pop().unsubscribe();
        }
        this.uppy.off( 'complete', () => {});
        this.uppy.close();
    }

    private listenUploadEventTrigger(): Observable<boolean> {
        return this.uploadEventTrigger.pipe(
            filter(( value: boolean ) => value === true ),
        );
    }

    private listenRemoveFileTrigger(): Observable<string> {
        return this.removeFileTrigger.pipe(
            filter(( value: string ) => value !== null ),
            tap(( fileId: string ) => this.removeFile( fileId )),
        );
    }

    private uploadEventHandler(): void {
        this.uppy.upload();
    }

    private removeFile( fileId: string ): void {
        this.uppy.removeFile( fileId );
    }
}
